import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle, Boxes, ChevronDown, ChevronRight, Lock, Pencil, Plus, Unlock,
} from 'lucide-react';

import { cn } from '@/lib/cn';
import { fmtDate, fmtInt, fmtUsd } from '@/lib/format';
import { useBoxes, useCreateBox, type Box } from './useBoxes';

type Tab = 'all' | 'draft' | 'confirmed' | 'review';

export function BoxesListPage() {
  const [tab, setTab] = useState<Tab>('all');
  const { data: boxes, isLoading } = useBoxes(
    tab === 'all' ? {}
      : tab === 'review' ? { needs_review: 1 }
      : { status: tab },
  );
  const create = useCreateBox();
  const [showNew, setShowNew] = useState(false);
  const [newBoxNumber, setNewBoxNumber] = useState('');
  const [newRows, setNewRows] = useState(2);
  const [newCols, setNewCols] = useState(3);

  const createBox = async () => {
    if (!newBoxNumber.trim()) return;
    const b = await create.mutateAsync({
      box_number: newBoxNumber.trim(),
      rows: newRows,
      cols: newCols,
    });
    setShowNew(false);
    setNewBoxNumber('');
    setNewRows(2);
    setNewCols(3);
    // Navegar al editor de la caja recién creada.
    window.location.href = `/boxes/${b.id}`;
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl">
      <header className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl md:text-2xl font-medium flex items-center gap-2">
            <Boxes size={22} className="text-notion-500" /> Cajas
          </h1>
          <p className="text-sm text-notion-500">
            Planificá la distribución de tus vinos en cajas físicas y validá una a una.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowNew(true)}
          className="btn-primary"
        >
          <Plus size={14} /> Nueva caja
        </button>
      </header>

      <div className="flex items-center border-b border-notion-200 mb-4">
        <TabButton active={tab === 'all'} onClick={() => setTab('all')}>Todas</TabButton>
        <TabButton active={tab === 'draft'} onClick={() => setTab('draft')}>En planificación</TabButton>
        <TabButton active={tab === 'confirmed'} onClick={() => setTab('confirmed')}>Cerradas</TabButton>
        <TabButton active={tab === 'review'} onClick={() => setTab('review')}>
          <span className="inline-flex items-center gap-1.5">
            <AlertTriangle size={12} className="text-rose-500" />
            A revisar
          </span>
        </TabButton>
      </div>

      {showNew && (
        <div className="card p-4 mb-4 space-y-3">
          <p className="font-medium text-notion-800">Nueva caja</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Número de caja</label>
              <input
                className="input"
                placeholder="ej. C1, 17, Cava-A-3"
                value={newBoxNumber}
                onChange={(e) => setNewBoxNumber(e.target.value)}
              />
            </div>
            <div>
              <label className="label">Dimensiones</label>
              <div className="flex items-center gap-1.5">
                <input
                  type="number"
                  min={1}
                  max={20}
                  className="input w-16 text-center"
                  value={newRows}
                  onChange={(e) => setNewRows(Math.max(1, Number(e.target.value)))}
                />
                <span className="text-notion-500 text-sm">filas ×</span>
                <input
                  type="number"
                  min={1}
                  max={20}
                  className="input w-16 text-center"
                  value={newCols}
                  onChange={(e) => setNewCols(Math.max(1, Number(e.target.value)))}
                />
                <span className="text-notion-500 text-sm">columnas</span>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button type="button" className="btn-secondary flex-1" onClick={() => setShowNew(false)}>
              Cancelar
            </button>
            <button
              type="button"
              className="btn-primary flex-1"
              onClick={createBox}
              disabled={!newBoxNumber.trim() || create.isPending}
            >
              Crear caja
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <p className="text-notion-500">Cargando…</p>
      ) : !boxes || boxes.length === 0 ? (
        <div className="card p-8 text-center text-notion-500">
          <Boxes size={32} className="mx-auto text-notion-300 mb-3" />
          {tab === 'review' ? (
            <>
              <p>¡Ninguna caja tiene problemas!</p>
              <p className="text-xs mt-1">
                Los slots apuntan a vinos con stock existente.
              </p>
            </>
          ) : (
            <>
              <p>Aún no creaste cajas.</p>
              <p className="text-xs mt-1">Tocá "Nueva caja" para empezar a planificar la distribución.</p>
            </>
          )}
        </div>
      ) : (
        <ul className="space-y-2">
          {boxes.map((b) => (
            <BoxRow key={b.id} box={b} />
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Fila de caja en el listado. Cabecera siempre visible (nº, dimensiones,
 * botellas y totales USD) y, al expandir, el detalle de vinos que contiene
 * con fecha/precio de compra y precio de mercado. Los datos vienen de
 * BoxResource (wine_lines + totals), calculados en BoxController::index.
 */
function BoxRow({ box }: { box: Box }) {
  const [open, setOpen] = useState(false);
  const total = box.rows * box.cols;
  const planned = box.planned_count ?? 0;
  const confirmed = box.confirmed_count ?? 0;
  const problems = box.problem_count ?? 0;
  const lines = box.wine_lines ?? [];
  const totals = box.totals;
  const bottles = totals?.bottles ?? 0;

  return (
    <li
      className={cn(
        'card overflow-hidden',
        problems > 0 && 'border-rose-200 bg-rose-50/40',
      )}
    >
      {/* Cabecera: toca para expandir el detalle de vinos. */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full p-3 flex items-center gap-3 text-left hover:bg-notion-50"
        aria-expanded={open}
      >
        <span
          className={cn(
            'w-9 h-9 rounded-md flex items-center justify-center shrink-0',
            box.status === 'confirmed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-50 text-amber-700',
          )}
        >
          {box.status === 'confirmed' ? <Lock size={14} /> : <Unlock size={14} />}
        </span>

        <span className="flex-1 min-w-0">
          <span className="font-medium text-notion-800 truncate flex items-center gap-1.5">
            {open ? <ChevronDown size={14} className="text-notion-400 shrink-0" />
              : <ChevronRight size={14} className="text-notion-400 shrink-0" />}
            Caja {box.box_number}
          </span>
          <span className="block text-xs text-notion-500">
            {box.rows}×{box.cols} ({fmtInt(total)} pos) · {fmtInt(confirmed)} confirmados ·{' '}
            {fmtInt(planned)} planificados · {fmtInt(total - confirmed - planned)} vacíos
          </span>
        </span>

        {/* Totales de la caja (USD). */}
        <span className="hidden sm:flex flex-col items-end text-xs shrink-0 mr-1">
          <span className="text-notion-800">
            {fmtInt(bottles)} bot · <strong>{fmtUsd(totals?.cost_usd)}</strong> costo
          </span>
          {totals?.market_usd != null && (
            <span className="text-notion-500">{fmtUsd(totals.market_usd)} mercado</span>
          )}
        </span>

        <span className="flex items-center gap-2 shrink-0">
          {problems > 0 && (
            <span
              className="text-[10px] uppercase tracking-wide px-2 py-1 rounded bg-rose-100 text-rose-700 inline-flex items-center gap-1"
              title="Slots con vinos borrados o sin stock"
            >
              <AlertTriangle size={10} />
              {problems}
            </span>
          )}
          <span
            className={cn(
              'text-[10px] uppercase tracking-wide px-2 py-1 rounded',
              box.status === 'confirmed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-50 text-amber-700',
            )}
          >
            {box.status === 'confirmed' ? 'Cerrada' : 'Planif.'}
          </span>
        </span>
      </button>

      {open && (
        <div className="border-t border-notion-200">
          {lines.length === 0 ? (
            <p className="px-3 py-4 text-sm text-notion-400 text-center">
              Esta caja no tiene vinos asignados todavía.
            </p>
          ) : (
            <>
              {/* Totales también en mobile (donde la cabecera los oculta). */}
              <div className="sm:hidden px-3 pt-2 text-xs text-notion-600">
                {fmtInt(bottles)} bot · <strong>{fmtUsd(totals?.cost_usd)}</strong> costo
                {totals?.market_usd != null && <> · {fmtUsd(totals.market_usd)} mercado</>}
              </div>

              <ul className="divide-y divide-notion-100">
                {lines.map((l) => (
                  <li key={l.wine_id} className="px-3 py-2 flex items-start gap-3 text-sm">
                    <div className="flex-1 min-w-0">
                      <p className="truncate text-notion-800">
                        {l.qty > 1 && <span className="text-notion-400">{l.qty}× </span>}
                        <span className={cn(l.deleted && 'line-through text-notion-400')}>
                          {l.name}
                        </span>
                        {l.vintage ? <span className="text-notion-500"> {l.vintage}</span> : null}
                      </p>
                      <p className="text-xs text-notion-500 truncate">
                        {[l.winery, l.format].filter(Boolean).join(' · ')}
                        {l.purchase_date ? ` · compra ${fmtDate(l.purchase_date)}` : ''}
                      </p>
                    </div>
                    <div className="text-right text-xs shrink-0 tabular-nums">
                      <p className="text-notion-800">{fmtUsd(l.unit_cost_usd)} costo</p>
                      <p className="text-notion-500">
                        {l.market_usd != null ? `${fmtUsd(l.market_usd)} mercado` : 'sin mercado'}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>

              {/* Pie: total por caja (ponderado por cantidad). */}
              <div className="px-3 py-2 border-t border-notion-200 bg-notion-50 flex items-center justify-between text-xs">
                <span className="text-notion-500">Total caja ({fmtInt(bottles)} botellas)</span>
                <span className="tabular-nums text-right">
                  <strong className="text-notion-800">{fmtUsd(totals?.cost_usd)}</strong> costo
                  {totals?.market_usd != null && (
                    <span className="text-notion-500"> · {fmtUsd(totals.market_usd)} mercado</span>
                  )}
                </span>
              </div>
            </>
          )}

          <div className="px-3 py-2 border-t border-notion-200 text-right">
            <Link
              to={`/boxes/${box.id}`}
              className="text-xs text-notion-600 inline-flex items-center gap-1 hover:text-notion-800"
            >
              <Pencil size={12} /> Abrir editor
            </Link>
          </div>
        </div>
      )}
    </li>
  );
}

function TabButton({
  active, onClick, children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'px-3 py-2 text-sm border-b-2 -mb-px transition',
        active
          ? 'border-notion-800 text-notion-800 font-medium'
          : 'border-transparent text-notion-500 hover:text-notion-800',
      )}
    >
      {children}
    </button>
  );
}
